import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';

import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { TiptapRichTextEditor } from 'components/DesignSystems/New/TiptapRichTextEditor';
import type { SitrepSectionConflictDetails } from 'components/DisasterGPT/hooks/useSitrepsV2';

type SitrepSectionConflictModalProps = {
  show: boolean;
  conflict: SitrepSectionConflictDetails | null;
  draftContent: string;
  onKeepExisting: () => void;
  onOverride: (latestVersionId: string, editedContent: string) => void;
  isOverriding?: boolean;
};

const formatTimestamp = (isoTimestamp: string): string => {
  const date = new Date(isoTimestamp);
  if (Number.isNaN(date.getTime())) return isoTimestamp;
  return date.toLocaleString();
};

const SitrepSectionConflictModal: React.FC<SitrepSectionConflictModalProps> = ({
  show,
  conflict,
  draftContent,
  onKeepExisting,
  onOverride,
  isOverriding = false,
}) => {
  const [editedContent, setEditedContent] = useState(draftContent);

  useEffect(() => {
    if (show) {
      setEditedContent(draftContent);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, conflict?.latestVersionId]);

  if (!conflict) return null;

  const otherUserName = conflict.latestUpdatedByUserName ?? 'another user';
  const updatedAt = formatTimestamp(conflict.latestVersionTimestamp);
  const latestText = conflict.latestVersionText ?? '';

  return (
    <Modal show={show} onHide={onKeepExisting} size="xl" centered>
      <Modal.Header closeButton closeVariant="white">
        <Modal.Title>
          This section was edited by another user
          <small className="fw-normal text-muted d-block mt-1">
            {otherUserName} saved changes
            {conflict.sectionName ? ` to ${conflict.sectionName}` : ''} since
            you started editing. <br />
            Review both versions below and choose whether to override with your
            edits or keep their content.
          </small>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="row g-3">
          <div className="col-12 col-md-6">
            <div className="d-flex flex-column h-100">
              <p className="mb-2 d-flex align-items-center">
                <span className={`status-dot danger`} />
                Current content from {otherUserName} at {updatedAt}
              </p>
              <div className="border rounded p-2 flex-grow-1">
                {latestText ? (
                  <TiptapRichTextEditor
                    value={latestText}
                    readOnly
                    showToolbar={false}
                    normalizeMarkdown
                  />
                ) : (
                  <em className="text-muted">No content.</em>
                )}
              </div>
              <div className="mt-2">
                <StylishNewButton
                  type="button"
                  className="button--secondary button--reverse"
                  disabled={isOverriding}
                  onClick={onKeepExisting}
                >
                  Keep current content
                </StylishNewButton>
              </div>
            </div>
          </div>
          <div className="col-12 col-md-6">
            <div className="d-flex flex-column h-100">
              <p className="mb-2 d-flex align-items-center">
                <span className={`status-dot success`} />
                Your edits
              </p>
              <div className="border rounded p-2 flex-grow-1">
                <TiptapRichTextEditor
                  value={editedContent}
                  onChange={(html) => setEditedContent(html)}
                  placeholder="Enter section content..."
                  disabled={isOverriding}
                  minHeight="150px"
                  className="w-100"
                  normalizeMarkdown
                />
              </div>
              <div className="mt-2">
                <StylishNewButton
                  type="button"
                  className="button--primary"
                  disabled={isOverriding}
                  onClick={() =>
                    onOverride(conflict.latestVersionId, editedContent)
                  }
                >
                  {isOverriding ? 'Overriding…' : 'Override with my edits'}
                </StylishNewButton>
              </div>
            </div>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default SitrepSectionConflictModal;
